import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import DiscoverSection from '../../components/ui/DiscoverSection';
import aiLearningPath from '../../assets/images/features/ai_learning_path.jpg';
import virtualTeacher from '../../assets/images/features/virtual_teacher.jpg';
import socialLearning from '../../assets/images/features/social_learning.jpg';

const Features = () => {

    return (
        <>
            <Helmet>
                <title>Features - Nexgen Learning - AI-Driven Education</title>
                <meta name="description" content="Discover NexGen Learning's innovative features including AI-powered learning paths, interactive content, and real-time progress tracking." />
            </Helmet>
            <Header />
            <NotificationBanner />
            <main>
                {/* Hero Section */}
                <section className="pt-28 pb-20 bg-gradient-to-b from-white to-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-5xl py-10 text-primary">|</h1>
                            <h1 className="text-4xl font-bold text-gray-900 mb-6">
                                Why Choose <span className="text-primary">NexGen Learning</span>?
                            </h1>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                                At NexGen Learning, we provide tailored solutions designed to meet the unique needs of various educational environments. Explore how our AI-driven tools can enhance learning experiences for schools, corporate training, and home education.
                            </p>
                        </div>
                    </div>
                </section>

                {/* AI-Powered Learning Path Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform rotate-3"></div>
                                <img
                                    src={aiLearningPath}
                                    alt="AI-Powered Learning Path"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>

                            {/* Right Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    AI-Powered Learning Path
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Tailored Learning for Every User
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    NexGen Learning's AI technology analyzes individual learning styles, preferences, and progress to generate a customized curriculum. This means that every student receives content that is best suited for them, ensuring they learn at their own pace and achieve their educational goals. With our adaptive learning paths, students can explore subjects deeply, reinforcing their strengths and addressing areas for improvement.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Personalized Curriculum</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Adaptive Learning</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Progress Tracking</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Virtual AI Teachers Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Virtual AI Teachers
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Adaptive Teaching Methods at Your Fingertips
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    Our Virtual AI Teachers engage students through interactive lessons that adapt in real-time based on their responses. Using advanced voice interaction capabilities, these AI instructors can answer questions, provide explanations, and offer encouragement, making learning a dynamic and interactive experience. This personalized approach ensures that each student receives the guidance they need to succeed, making education both effective and enjoyable.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Real-time Adaptation</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Voice Interaction</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">24/7 Availability</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform -rotate-3"></div>
                                <img
                                    src={virtualTeacher}
                                    alt="Virtual AI Teacher"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Social Learning Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Side - Image */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-2xl transform rotate-3"></div>
                                <img
                                    src={socialLearning}
                                    alt="Social Learning with AI Peers"
                                    className="relative rounded-2xl shadow-xl w-full h-auto"
                                />
                            </div>

                            {/* Right Side - Description */}
                            <div className="space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Social Learning with AI Peers
                                </h2>
                                <h3 className="text-xl text-primary font-semibold">
                                    Collaborate with AI Classmates in Real-Time
                                </h3>
                                <p className="text-lg text-gray-600 leading-relaxed">
                                    NexGen Learning fosters a collaborative environment where students can interact with AI classmates during group activities. This social learning aspect not only enhances critical thinking and problem-solving skills but also makes learning fun and engaging. By working together with AI peers, students can share ideas, tackle challenges, and learn the importance of teamwork—all in a virtual setting that encourages exploration and creativity.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Group Activities</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Real-time Collaboration</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                                        <span className="text-gray-700">Teamwork Skills</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <DiscoverSection />
            <Footer />
        </>
    );
};

export default Features; 